// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The Live view's measured HVAC electrical load, in one query.
 *
 * The BFF sums the equipment behind the site's chiller, chilled-water-pump and
 * CRAH nodes, so this is one request per poll where the original client-side
 * implementation was twenty — which is why that one was removed and the card
 * went forecast-only (ADR-0012). The fan-out is per device rather than per node
 * because the logical-node observations route has no data for these node types.
 */

import { getHvacLoad } from '../services/hvacLoadService';
import type { HvacLoadData } from '../services/hvacLoadService';
import type { LiveSeriesCoverage } from './useLivePlanTelemetry';
import { EMPTY_SERIES, useLiveComposedSeries } from './useLiveComposedSeries';
import type { TimeAxis } from '../types';

interface HvacLoadSeries {
  /**
   * Total HVAC electrical draw in kW on the plan axis — chillers, pumps and
   * CRAHs. Directly comparable to the plan's forecast: both are electrical.
   * Excludes DDC-cabinet auxiliary power, which nothing meters.
   */
  electricPower: (number | null)[];
}

export interface UseHvacLoadResult extends HvacLoadSeries {
  data: HvacLoadData | undefined;
  loading: boolean;
  coverage: LiveSeriesCoverage;
}

const EMPTY: HvacLoadSeries = { electricPower: EMPTY_SERIES };

export function useHvacLoad(
  siteId: string | undefined,
  orgId: string | undefined,
  timeAxis: TimeAxis | undefined
): UseHvacLoadResult {
  return useLiveComposedSeries<HvacLoadData, HvacLoadSeries>({
    name: 'hvac-load',
    // The org is required upstream, so the query stays idle until it resolves
    // rather than firing a request that can only 422.
    keyParts: [siteId, orgId],
    timeAxis,
    fetch: window => getHvacLoad(siteId as string, orgId as string, window),
    emptySeries: EMPTY,
    select: ({ charts }, { onAxis }) => {
      const electricPower = onAxis(charts.electricPower.values);
      return { primary: electricPower, series: { electricPower } };
    },
  });
}
